import * as React from "react";
import { Controller } from "react-hook-form";
import type {
  Control,
  FieldPath,
  FieldValues,
  RegisterOptions,
} from "react-hook-form";
import { Textarea } from "@/shared/ui/components/ui/textarea";
import { Label } from "@/shared/ui/components/ui/label";
import { cn } from "@/shared/lib/utils";
import { SendIcon } from "@/assets/svgs/sendIcon";
import { Button } from "@/shared/ui/components/custom/Button";

export interface TextAreaBaseProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  hint?: string;
  error?: string;
  maxLength?: number;
  showLength?: boolean;
  showSendButton?: boolean;
  onSend?: () => void;
  onErrorClear?: () => void;
  resizable?: boolean;
}

const TextAreaBase = React.forwardRef<HTMLTextAreaElement, TextAreaBaseProps>(
  (
    {
      className,
      label,
      hint,
      error,
      maxLength = 600,
      showLength = true,
      showSendButton = false,
      onSend,
      onErrorClear,
      disabled,
      id,
      onChange,
      value,
      placeholder,
      resizable = false,
      ...props
    },
    ref
  ) => {
    const textareaId = id || React.useId();
    const hasError = Boolean(error);
    const [isFocused, setIsFocused] = React.useState(false);
    const valueStr = value ? String(value) : "";
    const charCount = valueStr.length;
    const hasValue = charCount > 0;
    const isTyping = hasValue && isFocused;
    const canSend = hasValue && !disabled;

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (maxLength && e.target.value.length > maxLength) return;
      if (hasError) onErrorClear?.();
      onChange?.(e);
    };

    const handleSend = () => {
      if (canSend) onSend?.();
    };

    return (
      <div className="w-full gap-3">
        <div className="flex items-center justify-between mb-3">
          {label && (
            <Label
              htmlFor={textareaId}
              className={cn(
                "text-sm font-medium text-grey-900",
                hasError && "text-error-700"
              )}
            >
              {label}
            </Label>
          )}
         {showLength && <span
            className={cn(
              "text-xs font-medium text-grey-300",
              disabled && "text-grey-200",
              hasError && "text-error-700"
            )}
          >
            {charCount}/{maxLength} characters
          </span>}
        </div>

        <div
          className={cn(
            "relative group rounded-lg bg-white border transition-colors",
            !isFocused && "border-grey-200",
            isFocused && "border-brand-green",
            "hover:border-brand-green",
            disabled && "bg-grey-50 border-grey-200 hover:border-grey-200",
            hasError && "bg-error-bg border-error-700 hover:border-error-700"
          )}
        >
          <Textarea
            id={textareaId}
            ref={ref}
            disabled={disabled}
            value={value}
            placeholder={placeholder}
            maxLength={maxLength}
            onChange={handleChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            className={cn(
              "min-h-[180px] w-full border-0 bg-transparent px-4 py-3 text-sm shadow-none",
              showSendButton ? "rounded-t-lg" : "rounded-lg",
              "focus-visible:outline-none focus-visible:ring-0 transition-colors",
              !resizable && "resize-none",
              !hasValue && !isFocused && "text-grey-200",
              (hasValue || isFocused) && "text-grey-900",
              isTyping && "font-medium",
              disabled && "text-grey-200 cursor-not-allowed opacity-100",
              hasError && "text-error-700 placeholder:text-error-700",
              className
            )}
            {...props}
          />

          {showSendButton && (
            <>
              <div
                className={cn(
                  "border-t transition-colors",
                  hasError && "border-error-700",
                  !hasError &&
                    !disabled &&
                    (isFocused
                      ? "border-brand-green"
                      : "border-grey-200 group-hover:border-brand-green"),
                  disabled && "border-grey-200"
                )}
              />

              <div className="flex items-center px-4 py-3 rounded-b-lg">
                <Button
                  type="button"
                  onClick={handleSend}
                  disabled={!canSend}
                  rightIcon={<SendIcon size={16} color="white" />}
                  className={cn(
                    "!text-white",
                    canSend
                      ? "!bg-brand-green hover:!bg-brand-green"
                      : "!bg-green-300 cursor-not-allowed"
                  )}
                >
                  Send
                </Button>
              </div>
            </>
          )}
        </div>

        {(hint || error) && (
          <p
            className={cn(
              "mt-3 text-xs font-medium text-grey-300",
              hasError && "text-error-700"
            )}
          >
            {error || hint}
          </p>
        )}
      </div>
    );
  }
);

TextAreaBase.displayName = "TextAreaBase";

export interface TextAreaProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> extends Omit<TextAreaBaseProps, "name" | "value" | "onChange" | "error"> {
  name?: TName;
  control?: Control<TFieldValues>;
  rules?: Omit<
    RegisterOptions<TFieldValues, TName>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
  value?: string;
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement>;
  error?: string;
}

const TextArea = React.forwardRef(function TextAreaInner<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(
  props: TextAreaProps<TFieldValues, TName>,
  ref: React.Ref<HTMLTextAreaElement>
) {
  const { name, control, rules, error: errorProp, ...rest } = props;

  if (control && name) {
    return (
      <Controller<TFieldValues, TName>
        name={name}
        control={control}
        rules={rules}
        render={({ field, fieldState }) => (
          <TextAreaBase
            {...rest}
            {...field}
            ref={ref}
            value={field.value ?? ""}
            error={fieldState.error?.message || errorProp}
          />
        )}
      />
    );
  }

  return <TextAreaBase {...rest} ref={ref} error={errorProp} />;
}) as (<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(
  props: TextAreaProps<TFieldValues, TName> & {
    ref?: React.Ref<HTMLTextAreaElement>;
  }
) => React.ReactElement) & { displayName?: string };

TextArea.displayName = "TextArea";

export { TextArea };
